/**
 * Calendar event persistence adapter (records backend) — the documentsStore /
 * boardsStore shape over the generic extension_documents commands.
 *
 * One record per event in extension_documents (extension_id 'calendar',
 * record_type 'event'); `data` is the PLAN §3.2 client event record. Events
 * live in the PERSONAL scope: `personal:local` signed out (device-only),
 * `personal:<userId>` signed in — where the already-registered personal sync
 * engine (personalSync.js) pushes/pulls them across the user's devices on
 * paid plans, with zero calendar-specific backend code. Free/offline fully
 * works: the local SQLite rows ARE the calendar.
 *
 * Phase 1 is personal-only; team calendars (team:<teamId> scopes) are Phase 3.
 */
import { deleteRecord, listRecords, upsertRecord } from '../../../records/recordsClient';
import { personalScopeId } from '../../../services/personalScope';
import { teamScopeId } from '../task-manager/boardsStore';
import { useTeamsStore } from '../../../store/teamsStore';
import { capRefusal, isCapped } from '../../../plans/capRefusal';

export const EXTENSION_ID = 'calendar';
const RECORD_TYPE = 'event';

/* ── calendar keys ⇄ engine scopes (Phase 3) ──────────────────────────────
 * An event's `calendar` field (§3.2) is 'personal' or 'team:<teamId>'. Team
 * events live in the SAME `team:<teamId>` extension_documents bucket boards
 * and documents use (teamScopeId imported, not duplicated — the
 * documentsStore precedent), so they ride the realtime team engine task
 * ensureTeamBoardsSync already registers, with zero new registration code.
 * Server enforcement mirrors team sync: editor+ push, viewer read-only
 * (team_sync_controller.ex:23-24 — resolved by the orchestrator in source). */

export function calendarTeamId(calendarKey) {
  const m = /^team:(.+)$/.exec(String(calendarKey || ''));
  return m ? m[1] : null;
}

export function scopeForCalendar(calendarKey) {
  const teamId = calendarTeamId(calendarKey);
  return teamId != null ? teamScopeId(teamId) : personalScopeId();
}

/** `team:<id>` calendar keys for every team the user belongs to. */
function teamCalendarKeys() {
  try {
    const teams = useTeamsStore.getState().teams || [];
    return teams.filter((t) => t?.id != null).map((t) => `team:${t.id}`);
  } catch {
    return [];
  }
}

export function uid() {
  return (
    (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
    `evt-${Date.now()}-${Math.random().toString(16).slice(2)}`
  );
}

/** The extension_documents bucket for a calendar key (default: personal). */
export function recordsContext(calendarKey = 'personal') {
  return { workspaceId: scopeForCalendar(calendarKey) };
}

// The single rendering color for personal events (the app accent). Per-event
// colors were removed (owner, 30.08): coloring is BY CALENDAR — personal =
// accent, team = the team's color.
export const DEFAULT_EVENT_COLOR = '#8869eb';

/** Cap on an event's reminders[] — enforced on save AND on read (a team
 *  record is written by teammates; 500 reminders would toast every 30 s).
 *  Re-exported by mcp/calendarRecords.js for the published input schema. */
export const MAX_REMINDERS = 10;

function cleanReminders(reminders) {
  return Array.isArray(reminders)
    ? reminders
        .filter((r) => r && Number.isFinite(Number(r.minutes_before)))
        .slice(0, MAX_REMINDERS)
        .map((r) => ({ minutes_before: Number(r.minutes_before) }))
    : [];
}

/**
 * Build a complete PLAN §3.2 event record from a partial draft. Every field
 * is enumerated explicitly (boardsStore precedent: a field left out here is a
 * field that silently does not survive a save/sync/reload round-trip).
 */
export function normalizeEvent(draft = {}) {
  const nowIso = new Date().toISOString();
  const allDay = draft.all_day === true;
  return {
    uid: draft.uid || uid(),
    title: draft.title || '',
    description: draft.description || '',
    location: draft.location || '',
    // Timed events: ISO-8601 UTC instants, end exclusive. All-day events:
    // bare local YYYY-MM-DD keys instead (end_date inclusive).
    start: allDay ? null : draft.start || nowIso,
    end: allDay ? null : draft.end || draft.start || nowIso,
    all_day: allDay,
    start_date: allDay ? draft.start_date || null : null,
    end_date: allDay ? draft.end_date || draft.start_date || null : null,
    // IANA zone captured at creation — display/reference only (§3.2).
    tz:
      draft.tz ||
      (typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        : 'UTC'),
    calendar: draft.calendar || 'personal',
    // §3.2 AMENDMENT (owner, 30.08): `color` is DEPRECATED — no writer sets it
    // any more and rendering ignores it (calendar-based coloring instead).
    // Tolerated on read/pass-through: historical, imported and other-device
    // records may still carry a value and must survive a save round-trip.
    color: draft.color || null,
    reminders: cleanReminders(draft.reminders),
    // Phase 4 (§3.2 amendment, PLAN.recurrence §1): the RRULE-subset rule on
    // a MASTER record (null = single event), cancelled single occurrences,
    // and the override link on a per-instance exception record. Enumerated —
    // the P8 lesson: an un-enumerated field silently dies on save.
    recurrence: draft.recurrence || null,
    exdates: Array.isArray(draft.exdates) ? draft.exdates : [],
    override_of:
      draft.override_of?.master_uid && draft.override_of?.occurrence_date
        ? {
            master_uid: String(draft.override_of.master_uid),
            occurrence_date: draft.override_of.occurrence_date,
          }
        : null,
    // Phase 3 additive field (documented §3.2 extension): who created a TEAM
    // event ({id, name}) — PLAN §3.5 "team color, creator shown". Personal
    // events carry null (the owner is implicit in the scope).
    created_by: draft.created_by || null,
    // §3.2 `origin`: set on records materialized from an accepted invitation
    // ({organizer_id, appointment_uid, snapshot_version}) — drives the
    // update/cancel patching. Enumerated like every other field (a field left
    // out of this explicit build silently does not survive a save; Phase 2's
    // materialization wiring test caught exactly that).
    origin: draft.origin || null,
    status: draft.status === 'cancelled' ? 'cancelled' : 'confirmed',
    created_at: draft.created_at || nowIso,
    updated_at: nowIso,
  };
}

/** Flatten a raw records_list row (data spread flat by record_json) → event. */
function rowToEvent(row) {
  if (!row) return null;
  const { id, record_type, parent_id, sort_key, ...data } = row;
  return { ...data, uid: data.uid || id };
}

/**
 * One stored event as it reads from `calendarKey`'s scope. A TEAM scope is
 * authoritative for `calendar`: the record's own field is teammate-writable,
 * and a team event claiming 'personal' would show as the reader's own and
 * route its delete to the wrong scope. In the personal bucket the record's
 * field still wins (a stale team:<id> row there stays read-only until sync
 * tombstones it); the scope is only the fallback. Reminders are clamped on
 * read too — team rows never went through our save. The MCP read path uses
 * the same rule (card 25e3e5a2).
 */
export function eventInScope(event, calendarKey) {
  const isTeam = calendarTeamId(calendarKey) != null;
  return {
    ...event,
    calendar: isTeam ? calendarKey : event.calendar || calendarKey,
    reminders: cleanReminders(event.reminders),
  };
}

async function listEventsInCalendar(calendarKey) {
  const ctx = recordsContext(calendarKey);
  const rows = await listRecords({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordType: RECORD_TYPE,
    scopeId: null,
  });
  return (rows || [])
    .map(rowToEvent)
    .filter(Boolean)
    .map((e) => eventInScope(e, calendarKey));
}

/**
 * All events the user can see: the personal scope plus one `team:<teamId>`
 * scope per team (boardsStore's listBoardSummaries shape — a team scope that
 * has not synced yet simply returns no rows; per-scope failures never take
 * the whole calendar down).
 */
export async function listEvents() {
  const keys = ['personal', ...teamCalendarKeys()];
  const perScope = await Promise.all(
    keys.map((key) =>
      listEventsInCalendar(key).catch((err) => {
        console.warn(`[calendar] list failed for ${key}:`, err);
        return [];
      })
    )
  );
  return perScope.flat();
}

/**
 * Plans v2 local caps: the user's own events (personal calendar). An override
 * of one occurrence of a recurring series is part of that series, not an
 * event of its own.
 */
export async function countOwnEvents() {
  return (await ownEvents()).count;
}

/** The personal calendar's uids and its countable events, in one listing. */
async function ownEvents() {
  const list = await listEventsInCalendar('personal');
  return {
    uids: new Set(list.map((e) => String(e.uid))),
    count: list.filter((e) => !e.override_of).length,
  };
}

/**
 * Could this draft be a NEW personal event? (Not an override, and personal.)
 * Whether it is new is decided against the store, never by `uid` alone: an
 * .ics import (utils/ics.js) and a move from a team calendar carry a uid the
 * personal calendar has never seen.
 */
const isPersonalCandidate = (draft) =>
  !draft?.override_of && (draft?.calendar == null || draft.calendar === 'personal');

/** How many of `drafts` the personal calendar does not hold yet (by uid; no uid = new). */
function countNew(drafts, uids) {
  const seen = new Set();
  let adding = 0;
  for (const draft of drafts) {
    if (!isPersonalCandidate(draft)) continue;
    if (!draft.uid) {
      adding += 1;
      continue;
    }
    const key = String(draft.uid);
    if (uids.has(key) || seen.has(key)) continue;
    seen.add(key);
    adding += 1;
  }
  return adding;
}

/**
 * Plans v2: the refusal sentence when `drafts` would create more personal
 * events than the plan allows, or null. Lists the calendar only when the plan
 * caps events at all.
 */
async function eventCapRefusal(drafts) {
  if (!isCapped('calendarEvents') || !drafts.some(isPersonalCandidate)) return null;
  const own = await ownEvents();
  const adding = countNew(drafts, own.uids);
  return adding > 0 ? capRefusal('calendarEvents', own.count, adding) : null;
}

/** Thrown when the plan's cap refuses a create; carries the sentence. */
function planLimitError(message) {
  return Object.assign(new Error(message), { planLimit: true });
}

/**
 * Create or update one event. Normalizes the draft (updated_at bumps here),
 * writes it as a single record, and returns the persisted shape.
 */
export async function saveEvent(draft) {
  // Plans v2: a NEW personal event counts against the plan's local cap.
  const capped = await eventCapRefusal([draft]);
  if (capped) throw planLimitError(capped);
  const event = normalizeEvent(draft);
  // Routed by the event's OWN calendar key (§3.2) — personal bucket or the
  // team's realtime bucket.
  const ctx = recordsContext(event.calendar);
  await upsertRecord({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    record: {
      id: event.uid,
      record_type: RECORD_TYPE,
      // Override records mirror their master into the row's parent_id for DB
      // hygiene only — mapRow drops it, `data.override_of` is authoritative
      // (PLAN.recurrence §1).
      parent_id: event.override_of ? event.override_of.master_uid : null,
      sort_key: null,
      data: event,
    },
  });
  return event;
}

/** Tombstone one event in its calendar's scope (default personal). */
export async function deleteEvent(eventUid, calendarKey = 'personal') {
  const ctx = recordsContext(calendarKey);
  await deleteRecord({
    workspaceId: ctx.workspaceId,
    extensionId: EXTENSION_ID,
    recordId: String(eventUid),
  });
}

/** Import many parsed events (e.g. from .ics) sequentially; returns count. */
export async function importEvents(events) {
  // Plans v2: the whole import has to fit, or nothing is written.
  // Parsed events always carry a uid; one the personal calendar does not hold
  // yet is a new event (a re-import of the same file only updates).
  const capped = await eventCapRefusal(events || []);
  if (capped) throw planLimitError(capped);
  let imported = 0;
  for (const draft of events || []) {
    // eslint-disable-next-line no-await-in-loop
    await saveEvent(draft);
    imported += 1;
  }
  return imported;
}
